import { useCallback, useContext, useEffect, useState } from 'react';
import {
  IStationSwitcherContext,
  StationSwitcherContext,
} from '@components/providers/station/context.tsx';
import { IStation, Version } from '@helpers/types.ts';
import { useHttp } from '@hooks/use-http.ts';

export const useVersionList = () => {
  const http = useHttp();
  const { destStation: station } = useContext<IStationSwitcherContext>(
    StationSwitcherContext,
  );
  const [showInactive, setShowInactive] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [versions, setVersions] = useState<Array<Version>>([]);
  const [forDelete, setForDelete] = useState<Version | null>(null);
  const [forDisableEnable, setForDisableEnable] = useState<Version | null>(
    null,
  );

  const loadVersions = useCallback(
    (station: IStation, showInactive: boolean) => {
      setLoadingVersions(true);
      http.lastmile.getVersions(station.country, station.station, showInactive)
        .then((versions) => {
          setVersions(versions);
        })
        .finally(() => {
          setLoadingVersions(false);
        });
    },
    [http],
  );

  const reloadVersions = useCallback(() => {
    if (station) {
      loadVersions(station, showInactive);
    }
  }, [station, showInactive, loadVersions]);

  useEffect(() => {
    if (!station) return;
    loadVersions(station, showInactive);
  }, [station, showInactive, loadVersions]);

  const onDeleteVersion = useCallback((version: Version) => {
    setForDelete(version);
  }, []);
  const cancelDeleteVersion = useCallback(() => {
    setForDelete(null);
  }, []);
  const confirmDeleteVersion = useCallback(() => {
    setLoadingVersions(true);
    http.lastmile.deleteVersion(forDelete!._id).finally(() => {
      setForDelete(null);
      reloadVersions();
    });
  }, [forDelete, http, reloadVersions]);

  const onDisableEnableVersion = useCallback((version: Version) => {
    setForDisableEnable(version);
  }, []);
  const cancelDisableEnableVersion = useCallback(() => {
    setForDisableEnable(null);
  }, []);
  const confirmDisableEnableVersion = useCallback(() => {
    setLoadingVersions(true);
    if (forDisableEnable!.active)
      http.lastmile.disableVersion(forDisableEnable!._id).finally(() => {
        setForDisableEnable(null);
        reloadVersions();
      });
    else
      http.lastmile.enableVersion(forDisableEnable!._id).finally(() => {
        setForDisableEnable(null);
        reloadVersions();
      });
  }, [forDisableEnable, http, reloadVersions]);

  const toggleShowInactive = useCallback(() => {
    setShowInactive(!showInactive);
  }, [showInactive]);

  return {
    station,
    versions,
    forDelete,
    showInactive,
    forDisableEnable,
    loadingVersions,
    onDeleteVersion,
    cancelDeleteVersion,
    confirmDeleteVersion,
    onDisableEnableVersion,
    cancelDisableEnableVersion,
    confirmDisableEnableVersion,
    toggleShowInactive,
  };
};